import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TourProps } from 'antd'
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Result,
  Select,
  Space,
  Steps,
  Switch,
  Typography,
  message,
} from 'antd'
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { getAuth } from 'firebase/auth'

import {
  useLanguageSurvey,
  mergeQuestionsForLanguage,
  orderedQuestionsFromSet,
  LanguageQuestionGroup,
} from './hooks/useLanguageSurvey'
import { SurveyQuestion, SurveyResponseUpsertPayload } from './types/survey'
import { getLanguageDisplayName, getLanguageDisplayText } from './LanguageConfig'
import LoadingCard from './components/LoadingCard'
import AppPageHeader from './components/AppPageHeader'
import { resolveFrontendApiBase } from './utils/apiBase'
import AppTour, { renderAppTourPanel } from './tours/AppTour'
import { tourTarget, useGuidedTour } from './tours/useGuidedTour'
import './LanguageSurvey.css'

const { Text } = Typography

const SHORT_TEXT_MAX_LENGTH = 200
const LONG_TEXT_MAX_LENGTH = 2000
const API_BASE = resolveFrontendApiBase()

const getCurrentUserId = () => {
  try {
    return getAuth().currentUser?.uid
  } catch {
    return undefined
  }
}

const normalizeAnswerForQuestion = (question: SurveyQuestion, value: any) => {
  if (value === undefined || value === null) {
    return undefined
  }

  switch (question.type) {
    case 'boolean':
      return Boolean(value)
    case 'number':
    case 'rating': {
      const num = typeof value === 'number' ? value : Number(value)
      return Number.isFinite(num) ? num : undefined
    }
    case 'multiSelect':
      if (!Array.isArray(value)) {
        return undefined
      }
      return value.map((entry) => String(entry).trim()).filter(Boolean)
    case 'singleSelect': {
      const str = String(value).trim()
      return str || undefined
    }
    case 'shortText':
    case 'longText':
    default: {
      const str = String(value).trim()
      return str || undefined
    }
  }
}

const transformAnswersForSubmit = (
  questions: SurveyQuestion[],
  values: Record<string, any>
): Record<string, any> => {
  const payload: Record<string, any> = {}
  questions.forEach((question) => {
    const normalized = normalizeAnswerForQuestion(question, values[question.questionId])
    if (normalized !== undefined) {
      payload[question.questionId] = normalized
    }
  })
  return payload
}

const transformAnswerForForm = (question: SurveyQuestion, value: any) => {
  if (value === undefined || value === null) {
    return undefined
  }

  switch (question.type) {
    case 'boolean':
      return Boolean(value)
    case 'number':
    case 'rating':
      return typeof value === 'number' ? value : Number(value)
    case 'multiSelect':
      if (Array.isArray(value)) {
        return value.map((entry) => String(entry))
      }
      if (typeof value === 'string') {
        return [value]
      }
      return undefined
    default:
      return value
  }
}

const buildInitialValues = (
  questions: SurveyQuestion[],
  answers: Record<string, any> | undefined
): Record<string, any> => {
  const initial: Record<string, any> = {}
  const source = answers || {}

  questions.forEach((question) => {
    const existing = source[question.questionId]
    if (existing !== undefined && existing !== null) {
      initial[question.questionId] = transformAnswerForForm(question, existing)
    } else if (question.defaultValue !== undefined) {
      initial[question.questionId] = transformAnswerForForm(question, question.defaultValue)
    }
  })

  return initial
}

const LanguageSurveyWizard: React.FC = () => {
  const navigate = useNavigate()
  const {
    loading,
    saving,
    questions,
    response,
    error,
    saveAnswers,
    updateLanguageAnswers,
  } = useLanguageSurvey()
  const [form] = Form.useForm()
  const [currentStep, setCurrentStep] = useState(0)
  const [completed, setCompleted] = useState(false)
  const [manualEditMode, setManualEditMode] = useState(false)
  const [manualLanguage, setManualLanguage] = useState<string | null>(null)
  const hasRequestedRef = useRef(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const initialStepRenderRef = useRef(true)
  const tourUserId = getCurrentUserId()

  const selectedLanguages = response?.selectedLanguages || []

  const steps = useMemo(() => ['general', ...selectedLanguages], [selectedLanguages])
  const currentKey = steps[currentStep]

  useEffect(() => {
    if (loading) {
      hasRequestedRef.current = true
      return
    }
    if (!loading && hasRequestedRef.current) {
      setHasLoaded(true)
    }
  }, [loading])

  useEffect(() => {
    if (!hasLoaded) {
      return
    }
    if (!response || selectedLanguages.length === 0) {
      navigate('/language-survey', { replace: true })
    }
  }, [hasLoaded, navigate, response, selectedLanguages.length])

  useEffect(() => {
    if (!response || response.status !== 'completed') {
      setManualEditMode(false)
      setManualLanguage(null)
    }
  }, [response])

  useEffect(() => {
    if (manualEditMode) {
      setCompleted(false)
      return
    }
    if (!response) {
      setCompleted(false)
      return
    }
    const completedLanguages = response.progress?.completedLanguages || {}
    const allCompleted = selectedLanguages.length > 0
      ? selectedLanguages.every((language) => completedLanguages[language])
      : false
    const currentLanguage = response.progress?.currentLanguage
    const shouldBeCompleted = allCompleted && !currentLanguage
    setCompleted(shouldBeCompleted)
  }, [manualEditMode, response, selectedLanguages])

  useEffect(() => {
    if (manualEditMode) {
      return
    }
    if (!response) {
      return
    }
    const progressLanguage = response.progress?.currentLanguage
    if (progressLanguage) {
      const languageIndex = selectedLanguages.indexOf(progressLanguage)
      if (languageIndex >= 0) {
        setCurrentStep(languageIndex + 1)
        return
      }
    }
    setCurrentStep(0)
  }, [manualEditMode, response, selectedLanguages])

  useEffect(() => {
    if (initialStepRenderRef.current) {
      initialStepRenderRef.current = false
      return
    }
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [currentStep])

  const generalQuestions = useMemo(
    () => orderedQuestionsFromSet(questions?.general),
    [questions]
  )

  const languageQuestions = useMemo(() => {
    if (!questions) {
      return {}
    }
    const map: Record<string, LanguageQuestionGroup> = {}
    selectedLanguages.forEach((language) => {
      map[language] = mergeQuestionsForLanguage(questions, language)
    })
    return map
  }, [questions, selectedLanguages])

  const currentQuestions = useMemo(() => {
    if (currentKey === 'general') {
      return generalQuestions
    }
    return languageQuestions[currentKey]?.all || []
  }, [currentKey, generalQuestions, languageQuestions])

  const currentLanguageGroup = currentKey === 'general' ? null : languageQuestions[currentKey]

  useEffect(() => {
    form.resetFields()
    if (!currentQuestions.length) {
      return
    }
    const existingAnswers = currentKey === 'general'
      ? response?.answers?.general
      : response?.answers?.languages?.[currentKey]
    form.setFieldsValue(buildInitialValues(currentQuestions, existingAnswers))
  }, [currentQuestions, currentKey, form, response])

  const goToManageBooks = () => {
    navigate('/manage-books')
  }

  const handleBack = () => {
    if (currentStep === 0) {
      navigate('/language-survey')
      return
    }
    setCurrentStep((prev) => Math.max(prev - 1, 0))
  }

  const handleSubmitStep = useCallback(async () => {
    try {
      const values = await form.validateFields()
      const answersPayload = transformAnswersForSubmit(currentQuestions, values)

      if (currentKey === 'general') {
        const languagesAnswers = {
          ...(response?.answers?.languages || {}),
        }
        const completedLanguages = {
          ...(response?.progress?.completedLanguages || {}),
        }
        const nextStepKey = steps[currentStep + 1] || null
        const nextLanguage = manualEditMode
          ? manualLanguage || nextStepKey
          : selectedLanguages.find((language) => !completedLanguages[language]) || null
        const status = manualEditMode
          ? response?.status || 'completed'
          : nextLanguage
            ? response?.status || 'in_progress'
            : 'completed'
        const payload: SurveyResponseUpsertPayload = {
          selectedLanguages,
          answers: {
            general: answersPayload,
            languages: languagesAnswers,
          },
          progress: manualEditMode
            ? response?.progress
              ? {
                  ...response.progress,
                  completedLanguages,
                }
              : {
                  currentLanguage: nextLanguage,
                  completedLanguages,
                }
            : {
                currentLanguage: nextLanguage,
                completedLanguages,
              },
          status,
        }

        await saveAnswers(payload)

        if (manualEditMode) {
          if (nextLanguage && nextLanguage !== 'general') {
            const nextIndex = steps.indexOf(nextLanguage)
            if (nextIndex >= 0) {
              setManualLanguage(nextLanguage)
              setCurrentStep(nextIndex)
              const nextLabel = getLanguageDisplayText(nextLanguage)
              message.success(`General answers saved. Next: ${nextLabel}`)
              return
            }
          }
          setManualEditMode(false)
          setManualLanguage(null)
          setCompleted(true)
          message.success('General answers saved.')
          return
        }

        if (!nextLanguage) {
          setManualEditMode(false)
          setManualLanguage(null)
          setCompleted(true)
          message.success('Thanks! Your survey has been submitted.')
        } else {
          const nextIndex = steps.indexOf(nextLanguage)
          setCurrentStep(nextIndex >= 0 ? nextIndex : 1)
          message.success('General answers saved. Next up: language-specific questions.')
        }

        return
      }

      const completedLanguages = {
        ...(response?.progress?.completedLanguages || {}),
        [currentKey]: new Date().toISOString(),
      }
      const nextStepKey = steps[currentStep + 1]
      const nextLanguage =
        manualEditMode && manualLanguage
          ? manualLanguage
          : nextStepKey && nextStepKey !== 'general'
            ? nextStepKey
            : null
      const progress = {
        currentLanguage: nextLanguage,
        completedLanguages,
      }
      const allCompleted = selectedLanguages.every((language) => completedLanguages[language])

      await updateLanguageAnswers(currentKey, {
        answers: answersPayload,
        progress,
        status: allCompleted ? 'completed' : 'in_progress',
      })

      if (manualEditMode) {
        if (nextStepKey && nextStepKey !== 'general') {
          const nextLabel = getLanguageDisplayText(nextStepKey)
          setManualLanguage(nextStepKey)
          setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1))
          message.success(`Saved! Next: ${nextLabel}`)
        } else {
          const currentLabel = getLanguageDisplayText(currentKey)
          setManualEditMode(false)
          setManualLanguage(null)
          setCompleted(true)
          message.success(`${currentLabel} answers saved.`)
        }
        return
      }

      if (allCompleted) {
        setManualEditMode(false)
        setManualLanguage(null)
        setCompleted(true)
        message.success('Survey complete! Thank you for sharing your success.')
      } else {
        if (nextStepKey) {
          setCurrentStep(steps.indexOf(nextStepKey))
        }
        if (nextStepKey) {
          const nextLabel = nextStepKey === 'general' ? 'General' : getLanguageDisplayText(nextStepKey)
          message.success(`Saved! Next: ${nextLabel}`)
        }
      }
    } catch (err: any) {
      if (err?.errorFields) {
        message.warning('Please answer the required questions before continuing.')
        return
      }
      const detail = err?.response?.data?.detail
      message.error(detail || 'Unable to save your answers right now. Please try again.')
    }
  }, [
    currentKey,
    currentQuestions,
    currentStep,
    form,
    manualEditMode,
    manualLanguage,
    response,
    saveAnswers,
    selectedLanguages,
    steps,
    updateLanguageAnswers,
  ])

  const currentStepLabel = currentKey === 'general' ? 'General' : getLanguageDisplayName(currentKey)
  const currentStepDescription = currentKey === 'general'
    ? 'Start with a few questions about your overall translation success.'
    : `Tell us about your ${currentStepLabel} translations.`
  const progressLabel = steps.length > 0 ? `${currentStep + 1} of ${steps.length}` : 'No steps'
  const surveyWizardTour = useGuidedTour({
    userId: tourUserId,
    apiBase: API_BASE,
    flag: 'languageSurveyWizardTourSeen',
    enabled: true,
    autoStart: true,
    ready: !loading && currentQuestions.length > 0,
  })
  const surveyWizardTourSteps = useMemo<TourProps['steps']>(() => ([
    {
      title: 'Track survey progress',
      description: renderAppTourPanel('The progress panel shows general questions first, followed by each selected language.'),
      target: tourTarget('language-survey-wizard-progress'),
    },
    {
      title: 'Answer the current step',
      description: renderAppTourPanel('Required questions must be answered before the step can be saved.'),
      target: tourTarget('language-survey-wizard-form'),
    },
    {
      title: 'Move through the survey',
      description: renderAppTourPanel('Save & Continue stores this step and advances to the next language. The final step submits the survey.'),
      target: tourTarget('language-survey-wizard-actions'),
    },
  ]), [loading, currentQuestions.length])

  if (completed) {
    return (
      <main className="language-survey-shell">
        <div className="language-survey-inner language-survey-inner--narrow">
          <section className="language-survey-section language-survey-complete-state">
            <Result
              status="success"
              title="Survey complete!"
              subTitle="Thanks for sharing your results across languages."
              extra={[
                <Button key="manage" type="primary" className="app-primary-cta" onClick={goToManageBooks}>
                  Back to My Books
                </Button>,
                <Button key="languages" onClick={() => navigate('/language-survey')}>
                  Review languages
                </Button>,
                <Button
                  key="edit"
                  onClick={() => {
                    setManualEditMode(true)
                    setManualLanguage(null)
                    setCompleted(false)
                    setCurrentStep(0)
                  }}
                >
                  Edit answers
                </Button>,
              ]}
            />
          </section>
        </div>
      </main>
    )
  }

  return (
    <main className="language-survey-shell">
      <div className="language-survey-inner">
        <AppPageHeader
          kicker="Language survey"
          title="Language Success Survey"
          description={currentStepDescription}
          metrics={
            <>
              <span className="language-survey-pill is-info">{progressLabel}</span>
              {saving && <span className="language-survey-pill is-attention">Saving</span>}
            </>
          }
          actions={
            <Button icon={<QuestionCircleOutlined />} onClick={surveyWizardTour.start} data-tour-id="language-survey-wizard-tour-trigger" className="app-tour-trigger">
              Take tour
            </Button>
          }
          titleId="language-survey-wizard-title"
        />

        <section className="language-survey-workspace" aria-labelledby="language-survey-step-title">
          <section className="language-survey-section">
            <div className="language-survey-section-header">
              <div>
                <div className="language-survey-kicker">Current step</div>
                <h2 id="language-survey-step-title">{currentStepLabel}</h2>
                <p>{currentQuestions.length} question{currentQuestions.length === 1 ? '' : 's'} in this step.</p>
              </div>
              <span className="language-survey-pill is-info">{progressLabel}</span>
            </div>

          {error && (
            <Alert
              type="error"
              showIcon
              message="We ran into a problem loading your survey data. Try refreshing the page."
            />
          )}

            <div className="language-survey-progress-panel" data-tour-id="language-survey-wizard-progress">
              <Steps
                size="small"
                current={currentStep}
                responsive
                items={steps.map((key) => ({
                  key,
                  title: key === 'general' ? 'General' : getLanguageDisplayName(key),
                }))}
                onChange={(next) => {
                  if (next === currentStep) {
                    return
                  }
                  const targetKey = steps[next]
                  if (!targetKey) {
                    return
                  }
                  setManualEditMode(true)
                  setManualLanguage(targetKey === 'general' ? null : targetKey)
                  setCompleted(false)
                  setCurrentStep(next)
                }}
              />
            </div>

          {loading ? (
            <div className="language-survey-loading">
              <LoadingCard label="Loading questions..." />
            </div>
          ) : (
            <Form form={form} layout="vertical" disabled={saving} className="language-survey-question-form" data-tour-id="language-survey-wizard-form">
              {!currentQuestions.length ? (
                <Alert
                  type="info"
                  message="No questions are configured for this step."
                />
              ) : (
                (() => {
                  const renderQuestionField = (question: SurveyQuestion) => {
                    const isBoolean = question.type === 'boolean'
                    return (
                      <Form.Item
                        key={question.questionId}
                        label={
                          <Space direction="vertical" size={4}>
                            <Text strong>{question.prompt}</Text>
                            {question.description && (
                              <Text type="secondary">{question.description}</Text>
                            )}
                            {question.helpText && (
                              <Text type="secondary">{question.helpText}</Text>
                            )}
                          </Space>
                        }
                        name={question.questionId}
                        valuePropName={isBoolean ? 'checked' : undefined}
                        rules={question.isRequired ? [{ required: true, message: 'This question is required.' }] : undefined}
                      >
                        {(() => {
                          switch (question.type) {
                          case 'longText':
                            return (
                              <Input.TextArea
                                rows={4}
                                placeholder="Share the details"
                                allowClear
                                maxLength={LONG_TEXT_MAX_LENGTH}
                                showCount
                              />
                            )
                            case 'number':
                            case 'rating':
                              return <InputNumber style={{ width: '100%' }} placeholder="Enter a number" />
                            case 'singleSelect':
                              return (
                                <Select
                                  options={(question.options || []).map((option) => ({ value: option, label: option }))}
                                  placeholder="Select an option"
                                  allowClear
                                />
                              )
                            case 'multiSelect':
                              return (
                                <Select
                                  mode="multiple"
                                  options={(question.options || []).map((option) => ({ value: option, label: option }))}
                                  placeholder="Select all that apply"
                                  allowClear
                                />
                              )
                            case 'boolean':
                              return <Switch checkedChildren="Yes" unCheckedChildren="No" />
                          case 'shortText':
                          default:
                            return (
                              <Input
                                placeholder="Enter your answer"
                                allowClear
                                maxLength={SHORT_TEXT_MAX_LENGTH}
                              />
                            )
                          }
                        })()}
                      </Form.Item>
                    )
                  }

                  if (currentKey === 'general' || !currentLanguageGroup) {
                    return currentQuestions.map(renderQuestionField)
                  }

                  const sharedQuestions = currentLanguageGroup.shared || []
                  const specificQuestions = currentLanguageGroup.specific || []
                  const languageName = getLanguageDisplayName(currentKey)

                  return (
                    <>
                      <section className="language-survey-question-group" aria-labelledby="language-survey-shared-questions">
                        <div className="language-survey-question-group-header">
                          <div>
                            <div className="language-survey-kicker">Shared questions</div>
                            <h3 id="language-survey-shared-questions">Across every language</h3>
                          </div>
                          <span className="language-survey-pill is-info">{sharedQuestions.length} question{sharedQuestions.length === 1 ? '' : 's'}</span>
                        </div>
                        {sharedQuestions.length ? sharedQuestions.map(renderQuestionField) : (
                          <Alert type="info" showIcon message="No shared questions are configured for this language." />
                        )}
                      </section>
                      <section className="language-survey-question-group" aria-labelledby="language-survey-specific-questions">
                        <div className="language-survey-question-group-header">
                          <div>
                            <div className="language-survey-kicker">Language-specific questions</div>
                            <h3 id="language-survey-specific-questions">{languageName}</h3>
                          </div>
                          <span className="language-survey-pill is-info">{specificQuestions.length} question{specificQuestions.length === 1 ? '' : 's'}</span>
                        </div>
                        {specificQuestions.length ? specificQuestions.map(renderQuestionField) : (
                          <Alert type="info" showIcon message={`No ${languageName} questions are configured.`} />
                        )}
                      </section>
                    </>
                  )
                })()
              )}
            </Form>
          )}

          <div className="language-survey-footer" data-tour-id="language-survey-wizard-actions">
            <Button onClick={handleBack} icon={<ArrowLeftOutlined />} disabled={saving}>
              {currentStep === 0 ? 'Back to selection' : 'Previous'}
            </Button>
            <Button
              type="primary"
              className="app-primary-cta"
              loading={saving}
              icon={currentStep === steps.length - 1 ? <CheckOutlined /> : <ArrowRightOutlined />}
              onClick={handleSubmitStep}
            >
              {currentStep === steps.length - 1 ? 'Submit survey' : 'Save & Continue'}
            </Button>
          </div>
          </section>
        </section>
        <AppTour
          open={surveyWizardTour.open}
          steps={surveyWizardTourSteps}
          current={surveyWizardTour.current}
          onChange={surveyWizardTour.setCurrent}
          onClose={surveyWizardTour.close}
          onFinish={surveyWizardTour.close}
        />
      </div>
    </main>
  )
}

export default LanguageSurveyWizard
